/**
 * Auth provider – holds current Firebase user; syncs with Firestore user profile (role).
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../data/firebase/config';
import { userRepo } from '../../data/repositories/UserRepository';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      if (!firebaseUser) {
        setProfile(null);
        setLoading(false);
        return;
      }
      setLoading(true);
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      setLoading(false);
      return;
    }
    const unsub = userRepo.subscribe(user.uid, (snap) => {
      setProfile(snap || null);
      setLoading(false);
    });
    return () => unsub();
  }, [user?.uid]);

  const value = {
    user,
    profile,
    loading,
    userId: user?.uid ?? null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
